/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uiSelectors, useSetupDialog, uiTypes } from '@opengeoweb/store';
import { SoundingsManager } from './SoundingsManager';
import { soundingsActions } from '../../store/reducer';
import { getSoundingPanelsInOrder } from '../../store/selectors';
import type {
  SoundingsModuleState,
  SoundingsRootState,
} from '../../store/types';

export const SoundingsManagerConnect = (): React.ReactElement => {
  const { isDialogOpen, onCloseDialog, dialogOrder, setDialogOrder } =
    useSetupDialog(uiTypes.DialogTypes.SoundingsManager);

  const dispatch = useDispatch();
  const panelId = useSelector((store: SoundingsRootState) =>
    uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.SoundingsManager),
  );

  const soundingPanelsInOrder = useSelector((store: SoundingsModuleState) =>
    getSoundingPanelsInOrder(store, panelId),
  );

  const changeOrder = (
    newOrder: { id: string; name: string; isDisplayed: boolean }[],
  ): void => {
    const orderedIds = newOrder.map((collection) => collection.id);
    dispatch(
      soundingsActions.changeSoundingPanelOrder({
        panelId,
        newOrderOfIds: orderedIds,
        origin: 'soundingsManager',
      }),
    );
  };

  const toggleVisibility = (id: string): void => {
    dispatch(
      soundingsActions.toggleSoundingPanelVisibility({
        id,
        panelId,
        origin: 'soundingsManager',
      }),
    );
  };

  return (
    <SoundingsManager
      isOpen={isDialogOpen}
      onClose={onCloseDialog}
      onMouseDown={setDialogOrder}
      onOrderChange={changeOrder}
      onToggleVisibility={toggleVisibility}
      order={dialogOrder}
      soundingPanelsInOrder={soundingPanelsInOrder}
    />
  );
};
